'use client';

import { BoxCTA } from '@lichtblick/photon/atoms/box-cta/box-cta';
import { Text } from '@lichtblick/photon/atoms/text/text';
import { Checkbox } from '@lichtblick/photon/molecules/checkbox';
import { Stepper } from '@lichtblick/photon/molecules/stepper';
import { Textfield } from '@lichtblick/photon/molecules/textfield';
import { useTracker } from '@lichtblick/tracker';
import { type ChangeEventHandler, type FC, useCallback, useEffect, useState } from 'react';
import { CityInput } from '../OAllInOneCalculator/CityInput';
import {
  ELECTRICITY_USAGE,
  gasUsagePerSquareMeter,
  squareMeterLimit,
  usageLimitElectricity,
  usageLimitGas,
} from '../OAllInOneCalculator/constants';
import { UsageModal } from '../OAllInOneCalculator/UsageModal';
import { useVpCodes } from '../OAllInOneCalculator/useVpCodes';
import styles from './all-in-one-calculator.module.scss';

export const GasSubcalculator: FC = () => {
  const [postcode, setPostcode] = useState('');
  const [city, setCity] = useState('');

  const [people, setPeople] = useState(1);
  const [electricityUsage, setElectricityUsage] = useState(String(ELECTRICITY_USAGE[0]));

  const [addElectricity, setAddElectricity] = useState(false);
  const [space, setSpace] = useState(squareMeterLimit.default);
  const [gasUsage, setGasUsage] = useState(String(space * gasUsagePerSquareMeter));

  const [electricityUsageModalOpen, setElectricityUsageModalOpen] = useState(false);
  const [gasUsageModalOpen, setGasUsageModalOpen] = useState(false);

  const { computeVpCodes } = useVpCodes();
  const { trackCustomEvent } = useTracker();

  const cityChangeHandler = useCallback((city: string, postcode: string) => {
    setCity(city);
    setPostcode(postcode);
  }, []);

  const electricityChangeHandler = useCallback<ChangeEventHandler<HTMLInputElement>>((e) => {
    setElectricityUsage(e.currentTarget.value);
  }, []);

  const addElectricityChangeHandler = useCallback<ChangeEventHandler<HTMLInputElement>>((e) => {
    setAddElectricity(e.currentTarget.checked);
  }, []);

  const gasChangeHandler = useCallback<ChangeEventHandler<HTMLInputElement>>((e) => {
    setGasUsage(e.currentTarget.value);
  }, []);

  useEffect(() => {
    setElectricityUsage(String(ELECTRICITY_USAGE[Math.min(Math.max(people - 1, 0), 9)]));
  }, [people]);

  useEffect(() => {
    setGasUsage(String(space * gasUsagePerSquareMeter));
  }, [space]);

  const [vpCodes, setVpCodes] = useState<{
    uvp?: string;
    vp?: string;
  }>({});

  useEffect(() => {
    setVpCodes(computeVpCodes());
  }, [computeVpCodes]);

  return (
    <>
      <form
        action="/checkout/"
        method="GET"
        data-testid="gas-form"
        onSubmit={(e) => {
          if (Number(gasUsage) > usageLimitGas.max) {
            e.preventDefault();
            setGasUsageModalOpen(true);

            return;
          }

          if (addElectricity && Number(electricityUsage) > usageLimitElectricity.max) {
            e.preventDefault();
            setElectricityUsageModalOpen(true);

            return;
          }

          if (e.currentTarget.checkValidity()) {
            trackCustomEvent({
              eventName: 'tariff_calculator_show_tariffs',
              customProps: {
                product: 'ÖkoGas',
                zip_code: postcode,
                living_space: `${space} m²`,
                consumption_calculator: `${gasUsage} kWh/Jahr`,
                bundle_product: addElectricity,
              },
            });
          }
        }}
      >
        <div className={styles['input-grid']}>
          <div className={styles['input-row-1']}>
            <CityInput onChange={cityChangeHandler} />
            <Stepper
              defaultValue={space}
              label="Wohnfläche in m²"
              max={squareMeterLimit.max}
              min={squareMeterLimit.min}
              onChange={setSpace}
              step={squareMeterLimit.step}
            />
            <Textfield
              label="Gasverbrauch"
              min={usageLimitGas.min}
              name="gas"
              errorMessage={`Der Verbrauch muss über ${usageLimitGas.min} kWh/Jahr liegen.`}
              onChange={gasChangeHandler}
              rightSlot={
                <Text className={styles.unit} size="s">
                  kWh/Jahr
                </Text>
              }
              type="number"
              value={gasUsage}
              required
            />
          </div>

          {addElectricity && (
            <div className={styles['input-row-2']}>
              <Stepper defaultValue={1} label="Personenanzahl" max={10} min={1} onChange={setPeople} />
              <Textfield
                inputMode="numeric"
                label="Stromverbrauch"
                min={usageLimitElectricity.min}
                name="strom"
                errorMessage={`Der Verbrauch muss über ${usageLimitElectricity.min} kWh/Jahr liegen.`}
                onChange={electricityChangeHandler}
                rightSlot={<Text size="s">kWh/Jahr</Text>}
                type="number"
                value={electricityUsage}
                required
              />
            </div>
          )}
        </div>

        <input name="ort" type="hidden" value={city} />
        <input name="plz" type="hidden" value={postcode} />
        {Boolean(vpCodes.vp) && <input name="vp" type="hidden" value={vpCodes.vp} />}
        {Boolean(vpCodes.uvp) && <input name="uvp" type="hidden" value={vpCodes.uvp} />}
        <div className={styles['confirmation-row']}>
          <Checkbox label={<Checkbox.Label>ÖkoStrom* hinzufügen</Checkbox.Label>}>
            <Checkbox.Checkbox onChange={addElectricityChangeHandler} />
          </Checkbox>
          <BoxCTA
            as="button"
            type="submit"
            data-dd-action-name={`Calculator submit: Gas${addElectricity ? ' + ÖkoStrom' : ''}`}
          >
            Tarife berechnen
          </BoxCTA>
        </div>
      </form>
      <UsageModal isOpen={electricityUsageModalOpen} route="/gewerbestrom" setIsOpen={setElectricityUsageModalOpen} />
      <UsageModal isOpen={gasUsageModalOpen} route="/gewerbegas" setIsOpen={setGasUsageModalOpen} />
    </>
  );
};
